/**
 * Slanted-edge MTF (ISO 12233 style, after the sfrmat approach):
 * per-row edge centroids -> line fit -> 4x super-sampled edge spread function
 * -> line spread function (windowed) -> normalised Fourier magnitude.
 */
import type { Plane } from './types';

export interface MtfResult {
  /** Edge angle relative to the sampling axis, in degrees. */
  angleDeg: number;
  orientation: 'vertical' | 'horizontal';
  /** Super-sampled ESF / LSF positions (pixels, perpendicular to the edge). */
  esfX: Float64Array;
  esf: Float64Array;
  lsf: Float64Array;
  /** Spatial frequency in cycles per pixel. */
  freq: Float64Array;
  mtf: Float64Array;
  mtf50: number | null;
  mtf10: number | null;
  mtfAtNyquist: number;
  rows: number;
  warnings: string[];
}

const OVERSAMPLE = 4;

function hamming(i: number, n: number, center: number): number {
  const t = (i - center) / n;
  return Math.abs(t) > 0.5 ? 0 : 0.54 + 0.46 * Math.cos(2 * Math.PI * t);
}

function fitLine(ys: number[], xs: number[]): { a: number; b: number } {
  const n = xs.length;
  const mx = xs.reduce((p, q) => p + q, 0) / n;
  const my = ys.reduce((p, q) => p + q, 0) / n;
  let num = 0;
  let den = 0;
  for (let i = 0; i < n; i++) {
    num += (xs[i] - mx) * (ys[i] - my);
    den += (xs[i] - mx) ** 2;
  }
  const b = den ? num / den : 0;
  return { a: my - b * mx, b };
}

function firstBelow(freq: Float64Array, mtf: Float64Array, level: number): number | null {
  for (let i = 1; i < mtf.length; i++) {
    if (mtf[i] < level && mtf[i - 1] >= level) {
      return freq[i - 1] + ((level - mtf[i - 1]) / (mtf[i] - mtf[i - 1])) * (freq[i] - freq[i - 1]);
    }
  }
  return null;
}

/**
 * Compute the MTF from a rectangular region containing a single, slightly
 * slanted straight edge. The plane should hold a linear quantity.
 */
export function slantedEdgeMtf(plane: Plane, maxFreq = 1, freqStep = 0.005): MtfResult {
  const warnings: string[] = [];
  // Orientation: choose the axis with the stronger mean gradient as the sampling axis.
  let gx = 0;
  let gy = 0;
  const W = plane.width;
  const H = plane.height;
  const d = plane.data;
  for (let y = 1; y < H - 1; y++)
    for (let x = 1; x < W - 1; x++) {
      gx += Math.abs(d[y * W + x + 1] - d[y * W + x - 1]);
      gy += Math.abs(d[(y + 1) * W + x] - d[(y - 1) * W + x]);
    }
  const vertical = gx >= gy;
  const rows = vertical ? H : W;
  const cols = vertical ? W : H;
  const at = vertical ? (r: number, c: number) => d[r * W + c] : (r: number, c: number) => d[c * W + r];

  const empty = (msg: string): MtfResult => ({
    angleDeg: NaN,
    orientation: vertical ? 'vertical' : 'horizontal',
    esfX: new Float64Array(0),
    esf: new Float64Array(0),
    lsf: new Float64Array(0),
    freq: new Float64Array(0),
    mtf: new Float64Array(0),
    mtf50: null,
    mtf10: null,
    mtfAtNyquist: NaN,
    rows,
    warnings: [...warnings, msg],
  });
  if (cols < 12 || rows < 8) return empty('Region too small: need at least 12 px across and 8 px along the edge');

  // Edge polarity from the mean profile.
  let first = 0;
  let last = 0;
  for (let r = 0; r < rows; r++) {
    first += at(r, 0) + at(r, 1);
    last += at(r, cols - 1) + at(r, cols - 2);
  }
  const sign = last >= first ? 1 : -1;
  if (Math.abs(last - first) < 1e-12) return empty('No edge contrast found in the region');

  const centroids = (fit: { a: number; b: number } | null) => {
    const cs: number[] = [];
    const rs: number[] = [];
    for (let r = 0; r < rows; r++) {
      let num = 0;
      let den = 0;
      const center = fit ? fit.a + fit.b * r : (cols - 1) / 2;
      for (let c = 1; c < cols - 1; c++) {
        const g = (sign * (at(r, c + 1) - at(r, c - 1))) / 2;
        const w = fit ? hamming(c, cols, center) : 1;
        num += g * w * c;
        den += g * w;
      }
      if (den > 0) {
        cs.push(num / den);
        rs.push(r);
      }
    }
    return { cs, rs };
  };
  let { cs, rs } = centroids(null);
  if (cs.length < 4) return empty('Could not locate the edge in enough rows');
  let fit = fitLine(cs, rs);
  ({ cs, rs } = centroids(fit));
  fit = fitLine(cs, rs);
  const theta = Math.atan(fit.b);
  const angleDeg = (theta * 180) / Math.PI;
  if (Math.abs(angleDeg) < 1) warnings.push('Edge angle below 1°: super-sampling is poor (ISO 12233 recommends ~5°)');
  if (Math.abs(angleDeg) > 20) warnings.push('Edge angle above 20°: results may be inaccurate');
  const cosT = Math.cos(theta);

  // Project every pixel onto the edge normal and bin at 1/OVERSAMPLE pixel.
  const half = Math.floor(cols / 2);
  const nb = 2 * half * OVERSAMPLE;
  const sum = new Float64Array(nb);
  const cnt = new Float64Array(nb);
  for (let r = 0; r < rows; r++) {
    const xe = fit.a + fit.b * r;
    for (let c = 0; c < cols; c++) {
      const dist = (c - xe) * cosT;
      const bin = Math.floor((dist + half) * OVERSAMPLE);
      if (bin < 0 || bin >= nb) continue;
      sum[bin] += at(r, c);
      cnt[bin]++;
    }
  }
  // Fill empty bins by linear interpolation from neighbours.
  const esf = new Float64Array(nb);
  const filled: number[] = [];
  for (let i = 0; i < nb; i++) if (cnt[i] > 0) filled.push(i);
  if (filled.length < nb / 4) return empty('Too few samples along the edge normal');
  for (let i = 0; i < nb; i++) {
    if (cnt[i] > 0) {
      esf[i] = sum[i] / cnt[i];
      continue;
    }
    let lo = -1;
    let hi = -1;
    for (let k = i - 1; k >= 0; k--)
      if (cnt[k] > 0) {
        lo = k;
        break;
      }
    for (let k = i + 1; k < nb; k++)
      if (cnt[k] > 0) {
        hi = k;
        break;
      }
    if (lo < 0) esf[i] = sum[hi] / cnt[hi];
    else if (hi < 0) esf[i] = sum[lo] / cnt[lo];
    else esf[i] = sum[lo] / cnt[lo] + ((i - lo) / (hi - lo)) * (sum[hi] / cnt[hi] - sum[lo] / cnt[lo]);
  }
  // Trim bins at the ends that are only sparsely populated (corners of the ROI).
  const esfX = new Float64Array(nb);
  for (let i = 0; i < nb; i++) esfX[i] = (i + 0.5) / OVERSAMPLE - half;

  const lsf = new Float64Array(nb);
  for (let i = 1; i < nb - 1; i++) lsf[i] = (sign * (esf[i + 1] - esf[i - 1])) / 2;
  let pc = 0;
  let pw = 0;
  for (let i = 0; i < nb; i++) {
    pc += i * Math.max(lsf[i], 0);
    pw += Math.max(lsf[i], 0);
  }
  const center = pw > 0 ? pc / pw : nb / 2;
  for (let i = 0; i < nb; i++) lsf[i] *= hamming(i, nb, center);

  const nf = Math.floor(maxFreq / freqStep) + 1;
  const freq = new Float64Array(nf);
  const mtf = new Float64Array(nf);
  const delta = 1 / OVERSAMPLE;
  let dc = 0;
  for (let i = 0; i < nb; i++) dc += lsf[i];
  for (let k = 0; k < nf; k++) {
    const f = k * freqStep;
    let re = 0;
    let im = 0;
    for (let i = 0; i < nb; i++) {
      const ph = -2 * Math.PI * f * i * delta;
      re += lsf[i] * Math.cos(ph);
      im += lsf[i] * Math.sin(ph);
    }
    // Correct for the central-difference derivative: H(f) = sin(2 pi f d) / (2 pi f d).
    const w = 2 * Math.PI * f * delta;
    const corr = f > 0 ? Math.sin(w) / w : 1;
    freq[k] = f;
    mtf[k] = Math.hypot(re, im) / Math.abs(dc) / Math.max(corr, 0.1);
  }
  const nyq = Math.round(0.5 / freqStep);
  return {
    angleDeg,
    orientation: vertical ? 'vertical' : 'horizontal',
    esfX,
    esf,
    lsf,
    freq,
    mtf,
    mtf50: firstBelow(freq, mtf, 0.5),
    mtf10: firstBelow(freq, mtf, 0.1),
    mtfAtNyquist: nyq < nf ? mtf[nyq] : NaN,
    rows,
    warnings,
  };
}
